import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader, TablePagination } from "../reporting/ReportingShared";
import { isMerchantScoped, isReadOnly } from "../../constants/roles";
import { successToast, errorToast } from "../../shared/Toast";
import {
    listCampaigns,
    getCampaignApps,
    getCampaignLimits,
    cancelCampaign,
    duplicateCampaign,
    updateCampaign,
    getCampaign,
} from "../../utils/pushCampaignApi";
import { STATUS_META, SCREEN_LABELS, describeAudience } from "../../constants/pushCampaigns";
import CampaignComposerModal from "./CampaignComposerModal";
import CampaignDetailsModal from "./CampaignDetailsModal";

/**
 * Marketing → Push Campaigns.
 *
 * Merchant-scoped like every other page: a merchant user is pinned to its own merchant, a
 * platform admin uses the header switcher. If no active pickup app is linked to the
 * merchant the page says so and disables composing — the API refuses such campaigns too,
 * so this is the same rule surfaced early rather than a client-side substitute for it.
 *
 * The list polls every 5 s while a campaign is sending so the counters move on screen.
 */

const PAGE_LIMIT = 25;

const formatAgo = (iso) => {
    if (!iso) return "—";
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 0) return `in ${Math.ceil(-sec / 60)} min`;
    if (sec < 60) return "just now";
    if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
};

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white, color: C.text, outline: "none",
};
const buttonStyle = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", transition: "all 0.15s",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = { ...buttonStyle, background: C.white, color: C.textMed, border: `1px solid ${C.border}`, boxShadow: "none" };
const btnWarning = { ...buttonStyle, background: `linear-gradient(135deg, ${C.orange}, #FFB347)`, boxShadow: "0 4px 12px rgba(247,151,30,0.25)" };
const linkBtn = { padding: "4px 8px", border: "none", background: "none", color: C.coral, fontSize: 12, fontWeight: 600, cursor: "pointer" };

const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || { label: status, color: C.textMed, bg: C.borderLight };
    return <Badge color={m.color} bg={m.bg}>{status === "sending" ? "● " : ""}{m.label}</Badge>;
};

const PushCampaigns = () => {
    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const merchantId = isMerchantScoped(userType) ? loggedInMerchantId : reduxSelectedMerchant;
    const readOnly = isReadOnly(userType);
    const noMerchant = !merchantId || merchantId === "all";

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(PAGE_LIMIT);
    const [status, setStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [apps, setApps] = useState({ linked: true, apps: [] });
    const [limits, setLimits] = useState(null);

    const [composerOpen, setComposerOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [details, setDetails] = useState(null);
    const [confirm, setConfirm] = useState(null); // { kind: 'cancel' | 'sendNow', campaign }
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (noMerchant) { setItems([]); setTotal(0); return; }
        setLoading(true);
        const data = await listCampaigns({ merchantId, status: status === "all" ? undefined : status, search: search || undefined, page, limit });
        setItems(data.items || []);
        setTotal(data.total || 0);
        setLoading(false);
    }, [merchantId, noMerchant, status, search, page, limit]);

    const loadMeta = useCallback(async () => {
        if (noMerchant) return;
        const [a, l] = await Promise.all([getCampaignApps(merchantId), getCampaignLimits(merchantId)]);
        setApps(a);
        setLimits(l);
    }, [merchantId, noMerchant]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadMeta(); }, [loadMeta]);

    // Live counters while something is sending.
    const anySending = useMemo(() => items.some((c) => c.status === "sending" || (c.status === "scheduled" && c.scheduledAt && new Date(c.scheduledAt) <= new Date())), [items]);
    useEffect(() => {
        if (!anySending) return undefined;
        const t = setInterval(() => { load(); loadMeta(); }, 5000);
        return () => clearInterval(t);
    }, [anySending, load, loadMeta]);

    const refresh = async () => { await Promise.all([load(), loadMeta()]); };

    const openNew = () => { setEditing(null); setComposerOpen(true); };
    const openEdit = (c) => { setEditing(c); setComposerOpen(true); };
    const openDetails = async (c) => { setDetails(await getCampaign(c.id) || c); };

    const runConfirm = async () => {
        if (!confirm) return;
        setBusy(true);
        try {
            if (confirm.kind === "cancel") {
                const r = await cancelCampaign(confirm.campaign.id);
                if (r) successToast(confirm.campaign.status === "sending" ? "Cancelling — stops at the next batch" : "Campaign cancelled");
            } else if (confirm.kind === "sendNow") {
                const r = await updateCampaign(confirm.campaign.id, { sendMode: "now" }, null);
                if (r?.capBlocked) {
                    errorToast(
                        `${r.message || "Weekly limit reached."}${r.capBlocked.nextAllowedAt ? ` Next slot frees ${new Date(r.capBlocked.nextAllowedAt).toLocaleString()}.` : ""}`
                    );
                } else if (r) {
                    successToast("Campaign is being sent");
                }
            }
            setConfirm(null);
            await refresh();
        } finally {
            setBusy(false);
        }
    };

    const handleDuplicate = async (c) => {
        const r = await duplicateCampaign(c.id);
        if (r) { successToast("Draft copy created"); await refresh(); openEdit(r); }
    };

    const applySearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };

    const sentOnPage = items.reduce((s, c) => s + (c.sentCount || 0), 0);
    const openedOnPage = items.reduce((s, c) => s + (c.openedCount || 0), 0);

    return (
        <Fragment>
            <Breadcrumb title="Push Campaigns" parent="Marketing" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">
                    {noMerchant ? (
                        <RCard><div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Select a merchant to manage its push campaigns.</div></RCard>
                    ) : (
                        <>
                            {!apps.linked && (
                                <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 12, background: C.coralLight, color: C.coral, fontWeight: 600, fontSize: 13 }}>
                                    {apps.message || "No pickup app is linked to this merchant."} Campaigns cannot be sent until a platform admin links an active app under <b>Apps</b>.
                                </div>
                            )}

                            <div className="rpt-row" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
                                <Tile label="Campaigns" value={total} sub="for this merchant" />
                                <Tile
                                    label="Sends this week"
                                    value={limits ? `${limits.usedThisWeek} / ${limits.weeklyLimit}` : "—"}
                                    sub={limits?.nextAllowedAt ? `next slot ${formatAgo(limits.nextAllowedAt)}` : "rolling 7 days"}
                                    gradient={limits && limits.remaining === 0 ? `linear-gradient(90deg, ${C.coral}, #ff7a59)` : undefined}
                                />
                                <Tile label="Delivered" value={sentOnPage.toLocaleString()} sub="devices, campaigns on this page" />
                                <Tile label="Opened" value={sentOnPage ? `${Math.round((openedOnPage / sentOnPage) * 100)}%` : "—"} sub={`${openedOnPage.toLocaleString()} taps on this page`} />
                            </div>

                            <RCard>
                                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
                                    <form onSubmit={applySearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 260 }}>
                                        <input style={{ ...inputStyle, flex: 1 }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by name or title…" />
                                        <button type="submit" style={btnSecondary}>Search</button>
                                    </form>
                                    <select style={inputStyle} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                                        <option value="all">All statuses</option>
                                        {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                    </select>
                                    {!readOnly && (
                                        <button onClick={openNew} disabled={!apps.linked} style={{ ...buttonStyle, opacity: apps.linked ? 1 : 0.5, cursor: apps.linked ? "pointer" : "not-allowed" }}>
                                            ＋ New campaign
                                        </button>
                                    )}
                                </div>

                                {loading && items.length === 0 ? <Loader /> : items.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                        {search || status !== "all" ? "No campaigns match these filters." : "No campaigns yet. Create one to reach your customers."}
                                    </div>
                                ) : (
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    <th style={tbl.th}>Campaign</th>
                                                    <th style={tbl.th}>Audience</th>
                                                    <th style={tbl.th}>Opens</th>
                                                    <th style={tbl.th}>When</th>
                                                    <th style={tbl.th}>Status</th>
                                                    <th style={{ ...tbl.th, textAlign: "right" }}>Reach</th>
                                                    <th style={{ ...tbl.th, textAlign: "right" }}>Delivered</th>
                                                    <th style={{ ...tbl.th, textAlign: "right" }}>Opened</th>
                                                    <th style={tbl.th}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((c) => {
                                                    const editable = c.status === "draft" || c.status === "scheduled";
                                                    const cancellable = editable || c.status === "sending";
                                                    return (
                                                        <tr key={c.id}>
                                                            <td style={tbl.td}>
                                                                <div style={{ fontWeight: 700, color: C.text }}>{c.name}</div>
                                                                <div style={{ fontSize: 12, color: C.textMed, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.titleEn}>{c.titleEn}</div>
                                                                {c.app ? <div style={{ fontSize: 11, color: C.textLight }}>{c.app.name || c.app.reference}</div> : null}
                                                            </td>
                                                            <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>{describeAudience(c.audience)}</td>
                                                            <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>{c.deepLink?.screen === "product" ? `Product #${c.deepLink.productId}` : SCREEN_LABELS[c.deepLink?.screen] || "Home"}</td>
                                                            <td style={{ ...tbl.td, fontSize: 12, color: C.textMed, whiteSpace: "nowrap" }} title={c.scheduledAt ? new Date(c.scheduledAt).toLocaleString() : ""}>
                                                                {c.status === "draft" ? `draft · ${formatAgo(c.updatedAt)}` : c.finishedAt ? `finished ${formatAgo(c.finishedAt)}` : c.scheduledAt ? (new Date(c.scheduledAt) > new Date() ? `scheduled ${formatAgo(c.scheduledAt)}` : `started ${formatAgo(c.startedAt || c.scheduledAt)}`) : "—"}
                                                            </td>
                                                            <td style={tbl.td}><StatusBadge status={c.status} /></td>
                                                            <td style={{ ...tbl.td, textAlign: "right", fontSize: 12, whiteSpace: "nowrap" }}>{c.targetedUsers ? `${c.targetedUsers} / ${c.targetedTokens}` : "—"}</td>
                                                            <td style={{ ...tbl.td, textAlign: "right", fontSize: 12, whiteSpace: "nowrap" }}>
                                                                {c.status === "draft" || c.status === "scheduled" ? "—" : <span><b style={{ color: C.text }}>{c.sentCount}</b>{c.failedCount ? <span style={{ color: C.coral }}> · {c.failedCount} failed</span> : null}</span>}
                                                            </td>
                                                            <td style={{ ...tbl.td, textAlign: "right", fontSize: 12 }}>{c.sentCount ? `${c.openedCount} (${Math.round((c.openedCount / c.sentCount) * 100)}%)` : "—"}</td>
                                                            <td style={{ ...tbl.td, whiteSpace: "nowrap", textAlign: "right" }}>
                                                                <button style={linkBtn} onClick={() => openDetails(c)}>View</button>
                                                                {!readOnly && editable && <button style={linkBtn} onClick={() => openEdit(c)}>Edit</button>}
                                                                {!readOnly && editable && apps.linked && <button style={{ ...linkBtn, color: C.green }} onClick={() => setConfirm({ kind: "sendNow", campaign: c })}>Send now</button>}
                                                                {!readOnly && <button style={linkBtn} onClick={() => handleDuplicate(c)}>Duplicate</button>}
                                                                {!readOnly && cancellable && <button style={{ ...linkBtn, color: C.coral }} onClick={() => setConfirm({ kind: "cancel", campaign: c })}>Cancel</button>}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <TablePagination total={total} page={page} limit={limit} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
                                    </div>
                                )}
                            </RCard>
                        </>
                    )}
                </div>
            </Container>

            <CampaignComposerModal
                isOpen={composerOpen}
                onClose={() => setComposerOpen(false)}
                merchantId={merchantId}
                apps={apps.apps}
                limits={limits}
                campaign={editing}
                onSaved={refresh}
            />

            <CampaignDetailsModal
                isOpen={Boolean(details)}
                onClose={() => setDetails(null)}
                campaign={details}
                merchantId={merchantId}
                readOnly={readOnly}
            />

            <Modal isOpen={Boolean(confirm)} toggle={() => !busy && setConfirm(null)}>
                <ModalHeader toggle={() => !busy && setConfirm(null)}>
                    {confirm?.kind === "cancel" ? "Cancel this campaign?" : "Send this campaign now?"}
                </ModalHeader>
                <ModalBody style={{ color: C.textMed, lineHeight: 1.6 }}>
                    {confirm?.kind === "cancel" ? (
                        confirm.campaign.status === "sending"
                            ? <>It is being delivered right now. Cancelling stops at the next batch; <b style={{ color: C.text }}>{confirm.campaign.sentCount}</b> devices have already received it and cannot be recalled.</>
                            : <>“<b style={{ color: C.text }}>{confirm.campaign.name}</b>” will not be sent. This frees its slot in the weekly limit.</>
                    ) : confirm ? (
                        <>“<b style={{ color: C.text }}>{confirm.campaign.titleEn}</b>” goes to every customer in its audience immediately{limits ? <> and uses 1 of {limits.weeklyLimit} sends this week ({limits.usedThisWeek} used so far)</> : null}. It cannot be recalled once delivered.</>
                    ) : null}
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={() => setConfirm(null)} disabled={busy} style={btnSecondary}>Back</button>
                    <button onClick={runConfirm} disabled={busy} style={confirm?.kind === "cancel" ? btnWarning : buttonStyle}>
                        {busy ? "Working…" : confirm?.kind === "cancel" ? "Yes, cancel it" : "Yes, send now"}
                    </button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default PushCampaigns;
